import type { Router } from 'vue-router'
import { ElMessageBox } from 'element-plus'

interface AppVersionManifest {
  version?: string
  buildTime?: string
  buildId?: string
}

const CHECK_INTERVAL = 3 * 60 * 1000
const ROUTE_CHECK_GAP = 30 * 1000
const CHUNK_RELOAD_KEY = 'app:chunk-load-reload'

let isChecking = false
let hasUpdate = false
let lastCheckAt = 0

function getVersionUrl() {
  const base = import.meta.env.BASE_URL

  if (base && base !== './') {
    return `${base.replace(/\/$/, '')}/version.json`
  }

  const entryScript = Array.from(document.scripts)
    .map(item => item.src)
    .find(src => /\/assets\/.+\.(?:js|mjs)(?:\?|$)/.test(src))

  if (entryScript) {
    return entryScript.replace(/\/assets\/.*$/, '/version.json')
  }

  return `${window.location.origin}/version.json`
}

function appendNoCache(url: string) {
  return `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`
}

async function fetchLatestVersion() {
  const response = await fetch(appendNoCache(getVersionUrl()), {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
    },
  })

  if (!response.ok) {
    return null
  }

  return await response.json() as AppVersionManifest
}

function getCurrentBuildId() {
  return __SYSTEM_INFO__.buildId || __SYSTEM_INFO__.lastBuildTime || __SYSTEM_INFO__.pkg.version
}

function getLatestBuildId(version: AppVersionManifest) {
  return version.buildId || version.buildTime || version.version
}

function reloadPage() {
  window.location.reload()
}

async function showUpdateConfirm() {
  try {
    await ElMessageBox.confirm(
      '系统已发布新版本，刷新页面后即可使用最新功能。',
      '发现新版本',
      {
        type: 'info',
        confirmButtonText: '立即刷新',
        cancelButtonText: '稍后',
        closeOnClickModal: false,
        closeOnPressEscape: false,
      },
    )
    reloadPage()
  }
  catch {
    // 用户选择稍后刷新时保持当前页面可用，后续不重复弹窗打扰。
  }
}

async function checkForUpdate() {
  if (isChecking || hasUpdate) {
    return
  }

  isChecking = true
  lastCheckAt = Date.now()

  try {
    const latestVersion = await fetchLatestVersion()
    const latestBuildId = latestVersion ? getLatestBuildId(latestVersion) : ''
    if (latestBuildId && latestBuildId !== getCurrentBuildId()) {
      hasUpdate = true
      await showUpdateConfirm()
    }
  }
  catch {
    // 版本检测不能影响正常业务使用，网络异常时等待下次检测。
  }
  finally {
    isChecking = false
  }
}

function isChunkLoadError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error || '')

  return /Loading chunk \d+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(message)
}

function reloadOnceForChunkError() {
  const marker = `${getCurrentBuildId()}:${window.location.pathname}`

  if (sessionStorage.getItem(CHUNK_RELOAD_KEY) === marker) {
    return
  }

  sessionStorage.setItem(CHUNK_RELOAD_KEY, marker)
  reloadPage()
}

function setupChunkLoadErrorHandler() {
  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error || event.message)) {
      event.preventDefault()
      reloadOnceForChunkError()
    }
  }, true)

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault()
      reloadOnceForChunkError()
    }
  })
}

export function setupAppVersionChecker(router: Router) {
  if (import.meta.env.DEV) {
    return
  }

  setupChunkLoadErrorHandler()

  window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      void checkForUpdate()
    }
  }, CHECK_INTERVAL)

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkForUpdate()
    }
  })

  router.afterEach(() => {
    if (Date.now() - lastCheckAt > ROUTE_CHECK_GAP) {
      void checkForUpdate()
    }
  })

  void checkForUpdate()
}
